import { useState } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Accordion from "react-bootstrap/Accordion";
import TitleSection from "@/components/titleSection";

const ORANGE = "#e8973a";
const ORANGE_DARK = "#9c5a12"; /* WCAG AA contrast on white/light backgrounds */

// Default brand-level FAQ list. Pages can pass their own page-specific
// `faqData` prop (e.g. about page uses credibility questions, contact
// page uses process questions) to avoid duplicate FAQ content across
// pages. Each FAQ is engineered for answer-engine optimization (AEO)
// and featured-snippet eligibility: question phrasing matches natural
// voice queries, the first sentence states the answer, and supporting
// facts follow.
const FAQ_DATA = [
  {
    question: "Which is the best real estate developer in Nagpur?",
    answer:
      "Siddhivinayak Group is widely regarded as one of Nagpur's most trusted real estate developers. Backed by the Kunal Padole Group with 16+ years of leadership and MahaRERA registration (P50500078967), the developer delivers premium 2 & 3 BHK flats, plotted developments, and townships across Ram Nagar, Hanuman Nagar, Narsala, Bahadura, and Ambazari.",
  },
  {
    question: "Does Siddhivinayak Group offer RL plots in Nagpur?",
    answer:
      "Yes. Siddhivinayak Nagari by Siddhivinayak Group is a RERA-approved RL plot layout in Bahadura, Nagpur, offering 21 well-planned residential plots with 9-metre cement concrete roads, underground electric network, transformer, and a sewage treatment plant. Plots are ready for registry and construction.",
  },
  {
    question: "Why is Nagpur a smart city for real estate investment?",
    answer:
      "Nagpur is India's third Smart City under the SCM, hosts the MIHAN multi-modal international hub, and is on the Mumbai–Nagpur Samruddhi Expressway corridor. Combined with the operational Metro Phase-1 and an approved Phase-2, the city delivered 11–14% CAGR residential price growth between 2020 and 2025, making it a top-tier Tier-2 investment destination.",
  },
  {
    question: "What is the price of 2 BHK flats in Nagpur?",
    answer:
      "2 BHK flats in Nagpur's premium corridors (Ram Nagar, Hanuman Nagar, Ambazari) currently range between ₹45 lakh and ₹85 lakh depending on location, floor, and amenities. Siddhivinayak Group's Hilltop Heights -14 in Ram Nagar falls in the mid-premium band with MahaRERA pricing transparency.",
  },
  {
    question: "Is Siddhivinayak Group RERA registered?",
    answer:
      "Yes. Hilltop Heights -14 is registered with MahaRERA under registration number P50500078967. The project page on the official MahaRERA portal (maharera.mahaonline.gov.in) carries the complete project disclosure, layout plans, and quarterly progress updates as required by law.",
  },
  {
    question: "Which areas in Nagpur are best to buy a home?",
    answer:
      "Five localities currently lead Nagpur's residential growth: Ram Nagar (Hilltop) for premium hill-view flats near Ambazari Lake, Hanuman Nagar for connectivity near Krida Square, Narsala for upcoming Ayodhya City township, Bahadura for affordable RL plots, and Ambazari Layout for established family neighbourhoods. All five are covered by Siddhivinayak Group projects.",
  },
  {
    question: "How do I book a site visit for Siddhivinayak projects?",
    answer:
      "Book a free site visit in three steps: (1) call +91 98232 60303 or email mail@kunalpadole.com, (2) share the project name (Hilltop Heights -14, -11, Embassy-10, Siddhivinayak Nagari, or Ayodhya City), (3) pick a 60-minute slot — Saturday and Sunday slots are available, and a project consultant will accompany you on-site.",
  },
  {
    question: "Does Siddhivinayak Group assist with home loans?",
    answer:
      "Yes. Siddhivinayak Group has tie-ups with leading banks and housing finance companies (HFCs) including SBI, HDFC, ICICI, and LIC HFL. Pre-approved loan offers are available for select inventory, and the in-house team assists with documentation, eligibility checks, and disbursement at zero extra cost to the buyer.",
  },
  {
    question: "What documents are required to buy a flat in Nagpur?",
    answer:
      "For an RERA-registered project like Hilltop Heights -14, you need: PAN card, Aadhaar, last 6 months' bank statements, 2 passport photographs, address address of current residence, and a cancelled cheque. NRI buyers additionally need a passport copy, PIO/OCI card, and a notarised POA if purchasing through a representative.",
  },
  {
    question: "Who is the founder of Siddhivinayak Group?",
    answer:
      "Dr. Kunal G. Padole, an MBA and Ph.D. in Management with 16+ years of entrepreneurial experience, founded Siddhivinayak Infrastructure — the venture behind Siddhivinayak Group. Under his leadership, the group has expanded into real estate, education, and social initiatives, positively impacting lakhs of lives across Central India.",
  },
];

function FaqSection({
  sectionSpace,
  faqData,
  subTitle,
  title,
  intro,
  singleColumn = false,
  embedded = false,
}) {
  const data = Array.isArray(faqData) && faqData.length > 0 ? faqData : FAQ_DATA;
  const headingSub = subTitle ?? "FAQ";
  const headingTitle = title ?? "Frequently Asked Questions";
  const headingIntro =
    intro ??
    "Answers to what homebuyers and investors most often ask us about Siddhivinayak Group's projects in Nagpur.";
  const [activeKey, setActiveKey] = useState("0");

  return (
    <div
      className={`ltn__faq-area faq-theme${
        embedded ? " faq-theme--embedded" : ""
      } ${sectionSpace || ""}`.trim()}
    >
      <Container fluid={embedded} className={embedded ? "px-0" : undefined}>
        {/* Centered header — matches the "Our Developments / Explore Our
            Projects" pattern used elsewhere on the homepage. */}
        <Row>
          <Col xs={12} lg={12} xl={12} className="mx-auto">
            <TitleSection
              sectionClasses="text-center"
              headingClasses="section-subtitle-2"
              titleSectionData={{
                subTitle: headingSub,
                title: headingTitle,
              }}
            />
            {headingIntro ? (
              <p
                className="faq-intro"
                style={{
                  color: "#525a64",
                  width: "100%",
                  margin: "20px auto 20px",
                }}
              >
                {headingIntro}
              </p>
            ) : null}
          </Col>
        </Row>

        <Row>
          {/* Accordion now spans full width — the "Still have questions?"
              support card was removed so the FAQ list takes the entire
              row. Items render in a 2-column CSS grid on desktop. */}
          <Col
            xs={12}
            lg={embedded ? 12 : singleColumn ? 8 : 11}
            className="mx-auto"
          >
            <Accordion
              activeKey={activeKey}
              onSelect={(key) => setActiveKey(key)}
              className={`faq-accordion-grid${
                singleColumn ? " faq-accordion-grid--single" : ""
              }`}
            >
              {data.map((item, index) => (
                <Accordion.Item
                  eventKey={String(index)}
                  key={index}
                  className="faq-item"
                >
                  <Accordion.Header as="h3">
                    <span className="faq-number">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <span className="faq-question">{item.question}</span>
                  </Accordion.Header>
                  <Accordion.Body>{item.answer}</Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
          </Col>
        </Row>
      </Container>

      <style jsx global>{`
        .faq-theme .faq-intro {
          text-align: center !important;
        }
        .faq-theme .faq-accordion-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 16px;
        }
        .faq-theme .faq-accordion-grid .faq-item {
          margin-bottom: 0;
          align-self: start;
        }
        @media (min-width: 768px) {
          .faq-theme .faq-accordion-grid {
            grid-template-columns: 1fr 1fr;
          }
          .faq-theme .faq-accordion-grid.faq-accordion-grid--single {
            grid-template-columns: minmax(0, 1fr);
          }
        }

        .faq-theme .faq-item {
          background: #ffffff;
          border: none;
          border-radius: 10px;
          overflow: hidden;
          margin-bottom: 16px;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
          transition: box-shadow 0.25s ease;
        }
        .faq-theme .faq-item:last-child {
          margin-bottom: 0;
        }
        .faq-theme .faq-item .accordion-button {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 22px 25px;
          font-family: inherit;
          background: #fff;
        }
        .faq-theme .faq-item .accordion-button:focus {
          box-shadow: none;
          border-color: transparent;
        }
        .faq-theme .faq-item .accordion-button::after {
          filter: invert(56%) sepia(78%) saturate(430%) hue-rotate(1deg)
            brightness(96%) contrast(93%);
        }
        .faq-theme .faq-item .accordion-button:not(.collapsed) {
          background: #fff;
          box-shadow: none;
        }
        .faq-theme .faq-item .accordion-button:not(.collapsed) .faq-question {
          color: ${ORANGE_DARK};
        }
        .faq-theme .faq-item .accordion-button:not(.collapsed) .faq-number {
          background: ${ORANGE_DARK};
          color: #fff;
        }
        .faq-theme .faq-number {
          min-width: 34px;
          height: 34px;
          border-radius: 50%;
          background: #f7f4ef;
          color: ${ORANGE_DARK};
          font-size: 13px;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: all 0.25s ease;
        }
        .faq-theme .faq-question {
          font-weight: 600;
          font-size: 16px;
          color: #1a1a1a;
          transition: color 0.25s ease;
        }
        .faq-theme .accordion-body {
          padding: 0 25px 25px 75px;
          color: #525a64;
          line-height: 1.7;
        }

        @media (max-width: 991px) {
          .faq-theme .faq-side-panel {
            height: auto;
          }
        }
      `}</style>
    </div>
  );
}

export default FaqSection;
